import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { 
  Calendar as CalendarIcon, Clock, Check, X, Loader2, 
  ChevronLeft, ChevronRight, Video, MapPin, AlertCircle
} from 'lucide-react';

/**
 * YouthSelfScheduling - Self-scheduling appointments for Young Adults (16-17)
 */

const TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
  '4:00 PM', '4:30 PM', '5:00 PM'
];

const SESSION_TYPES = [
  { id: 'individual', name: 'Individual Session', duration: 50, icon: '👤' },
  { id: 'checkin', name: 'Quick Check-in', duration: 25, icon: '💬' },
];

const YouthSelfScheduling = ({ clientId, token, backendUrl, toast }) => {
  const [step, setStep] = useState(1); // 1: Select Type, 2: Select Date, 3: Select Time, 4: Confirm
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [therapist, setTherapist] = useState(null);

  const fetchAvailability = useCallback(async (date) => {
    if (!date) return;
    
    try {
      setIsLoading(true);
      const dateStr = date.toISOString().split('T')[0];
      const response = await fetch(
        `${backendUrl}/api/youth-portal/scheduling/availability?client_id=${clientId}&date=${dateStr}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.available_slots || []);
        setTherapist(data.therapist || null);
      }
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, clientId, token]);

  const fetchUpcoming = useCallback(async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/youth-portal/scheduling/upcoming?client_id=${clientId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setUpcomingAppointments(data.appointments || []);
      }
    } catch (err) {
      console.error('Error fetching upcoming:', err);
    }
  }, [backendUrl, clientId, token]);

  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate, fetchAvailability]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!selectedType || !selectedDate || !selectedTime) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/youth-portal/scheduling/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: clientId,
          session_type: selectedType.id,
          date: selectedDate.toISOString().split('T')[0],
          time: selectedTime,
          duration: selectedType.duration
        })
      });

      const data = await response.json();

      if (data.success) {
        toast?.success?.('Appointment scheduled successfully!');
        setStep(1);
        setSelectedType(null);
        setSelectedDate(null);
        setSelectedTime(null);
        fetchUpcoming();
      } else {
        toast?.error?.(data.detail || 'Failed to schedule appointment');
      }
    } catch (err) {
      console.error('Error booking:', err);
      toast?.error?.('Failed to schedule appointment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;

    try {
      const response = await fetch(
        `${backendUrl}/api/youth-portal/scheduling/cancel/${appointmentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast?.success?.('Appointment cancelled');
        fetchUpcoming();
      } else {
        toast?.error?.('Failed to cancel appointment');
      }
    } catch (err) {
      console.error('Error cancelling:', err);
      toast?.error?.('Failed to cancel appointment');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  // Disable past dates and weekends
  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = date.getDay();
    return date < today || day === 0 || day === 6;
  };

  return (
    <div className="space-y-6">
      {/* Upcoming Appointments */}
      {upcomingAppointments.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              Upcoming Appointments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingAppointments.map((appt) => (
                <div key={appt.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                  <div>
                    <p className="font-medium text-gray-900">{appt.session_type_name || 'Session'}</p>
                    <p className="text-sm text-gray-600">
                      {formatDate(appt.date)} at {appt.time}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-blue-600 border-blue-200">
                      {appt.duration} min
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelAppointment(appt.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduling Flow */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Schedule a Session</CardTitle>
          <CardDescription>
            {step === 1 && 'Choose the type of session you need'}
            {step === 2 && 'Select a date for your appointment'}
            {step === 3 && 'Choose an available time slot'}
            {step === 4 && 'Review and confirm your appointment'}
          </CardDescription>
          
          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s <= step 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`w-8 h-1 ${s < step ? 'bg-purple-600' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Step 1: Session Type */}
          {step === 1 && (
            <div className="grid gap-4">
              {SESSION_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => { setSelectedType(type); setStep(2); }}
                  className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <div className="text-3xl">{type.icon}</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{type.name}</p>
                    <p className="text-sm text-gray-600">{type.duration} minutes</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Date Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <Button variant="ghost" onClick={() => setStep(1)} className="mb-2">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={disabledDays}
                  className="rounded-md border"
                />
              </div>
            </div>
          )}

          {/* Step 3: Time Selection */}
          {step === 3 && (
            <div className="space-y-4">
              <Button variant="ghost" onClick={() => setStep(2)} className="mb-2">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              
              <div className="text-center mb-4">
                <p className="font-medium text-gray-900">{formatDate(selectedDate)}</p>
                <p className="text-sm text-gray-600">with {therapist?.name || 'your therapist'}</p>
              </div>

              {isLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
                  <p className="text-gray-600 mt-2">Loading available times...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No available times on this date</p>
                  <Button variant="link" onClick={() => setStep(2)}>
                    Choose another date
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => { setSelectedTime(slot); setStep(4); }}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        selectedTime === slot
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 mx-auto mb-1 text-gray-400" />
                      <span className="text-sm font-medium">{slot}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-4">
              <Button variant="ghost" onClick={() => setStep(3)} className="mb-2">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>

              <div className="bg-purple-50 rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-lg text-purple-900 text-center">
                  Confirm Your Appointment
                </h3>
                
                <div className="space-y-3 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Session Type</p>
                    <p className="font-medium text-gray-900">{selectedType?.icon} {selectedType?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date & Time</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedDate)}</p>
                    <p className="font-medium text-gray-900">{selectedTime}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-medium text-gray-900">{selectedType?.duration} minutes</p>
                  </div>
                  {therapist && (
                    <div>
                      <p className="text-sm text-gray-600">Therapist</p>
                      <p className="font-medium text-gray-900">{therapist.name}</p>
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Confirm Appointment
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default YouthSelfScheduling;
